import React, { useState } from 'react';
import { useStore } from '../../store';
import { analyzeSMC } from '../../engines/smcEngine';
import { SMCResult, SMCOverlaySettings, FairValueGap, OrderBlock, StructureEvent } from '../../types';

// ─── Overlay toggle bar ───────────────────────────────────────────────────────

interface OverlayToggleProps {
  overlays: SMCOverlaySettings;
  onChange: (o: SMCOverlaySettings) => void;
}

function OverlayToggles({ overlays, onChange }: OverlayToggleProps) {
  const toggle = (key: keyof SMCOverlaySettings) =>
    onChange({ ...overlays, [key]: !overlays[key] });

  const items: [keyof SMCOverlaySettings, string][] = [
    ['showBOSCHOCH', 'BOS/CHOCH'],
    ['showFVGs', 'FVGs'],
    ['showOrderBlocks', 'Order Blocks'],
    ['showSweeps', 'Sweeps'],
    ['showPremiumDiscount', 'Prem/Disc'],
  ];

  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
      {items.map(([key, label]) => (
        <button
          key={key}
          onClick={() => toggle(key)}
          style={{
            padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            fontFamily: 'var(--sans)', cursor: 'pointer', letterSpacing: '.4px',
            border: overlays[key] ? '1px solid rgba(45,156,219,.45)' : '1px solid rgba(255,255,255,.1)',
            background: overlays[key] ? 'rgba(45,156,219,.12)' : 'transparent',
            color: overlays[key] ? 'var(--blue)' : 'var(--t3)',
            transition: 'all .12s',
          }}
        >
          {overlays[key] ? '◉' : '○'} {label}
        </button>
      ))}
    </div>
  );
}

// ─── Bias indicator ───────────────────────────────────────────────────────────

function BiasIndicator({ bias, strength }: { bias: string; strength: string }) {
  const c = bias === 'bullish' ? 'var(--green)' : bias === 'bearish' ? 'var(--red)' : 'var(--amber)';
  const bg = bias === 'bullish' ? 'var(--green-bg)' : bias === 'bearish' ? 'var(--red-bg)' : 'var(--amber-bg)';
  const bd = bias === 'bullish' ? 'var(--green-bd)' : bias === 'bearish' ? 'var(--red-bd)' : 'var(--amber-bd)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ padding: '4px 14px', borderRadius: 20, background: bg, border: `1px solid ${bd}`, color: c, fontSize: 14, fontWeight: 700 }}>
        {bias === 'neutral' ? 'Neutral' : bias === 'bullish' ? '▲ Bullish' : '▼ Bearish'}
      </div>
      <span style={{ fontSize: 11, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.8px' }}>{strength} bias</span>
    </div>
  );
}

// ─── Structure event row ──────────────────────────────────────────────────────

function StructureRow({ event }: { event: StructureEvent }) {
  const isBOS = event.type === 'BOS';
  const isBull = event.dir === 'bullish';
  const c = isBull ? 'var(--green)' : 'var(--red)';
  const tag = isBOS ? 'b-blue' : isBull ? 'b-green' : 'b-red';
  return (
    <div className="card-inner" style={{ borderLeft: `2px solid ${c}`, marginBottom: 4 }}>
      <div className="fsb" style={{ marginBottom: 3 }}>
        <div className="flex gap6">
          <span className={`badge ${tag}`} style={{ fontSize: 10 }}>{event.type}</span>
          <span className={`badge ${event.strength === 'major' ? 'b-amber' : 'b-dim'}`} style={{ fontSize: 10 }}>{event.strength}</span>
          <span className="mono" style={{ fontSize: 12, color: c }}>{event.price.toFixed(2)}</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--t3)' }}>{isBull ? '▲' : '▼'} {event.dir}</span>
      </div>
      <div className="inferred">{event.description}</div>
    </div>
  );
}

// ─── FVG row ──────────────────────────────────────────────────────────────────

function FVGRow({ fvg, curPrice }: { fvg: FairValueGap; curPrice: number }) {
  const isBull = fvg.dir === 'bullish';
  const c = isBull ? 'var(--green)' : 'var(--red)';
  const dist = Math.abs(curPrice - fvg.midpoint);
  const distPct = (dist / curPrice * 100).toFixed(2);
  return (
    <div className="card-inner" style={{ borderLeft: `2px solid ${c}`, marginBottom: 4 }}>
      <div className="fsb" style={{ marginBottom: 3 }}>
        <div className="flex gap6">
          <span className={`badge ${isBull ? 'b-green' : 'b-red'}`} style={{ fontSize: 10 }}>FVG {isBull ? '▲' : '▼'}</span>
          <span className={`badge ${fvg.significance === 'major' ? 'b-amber' : 'b-dim'}`} style={{ fontSize: 10 }}>{fvg.significance}</span>
          <span className="mono" style={{ fontSize: 12, color: c }}>{fvg.bottom.toFixed(2)}–{fvg.top.toFixed(2)}</span>
        </div>
        <div className="flex gap6">
          <span style={{ fontSize: 11, color: 'var(--t3)' }}>{distPct}% away</span>
          {fvg.status === 'partially_mitigated' && <span className="badge b-amber" style={{ fontSize: 7 }}>PARTIAL</span>}
        </div>
      </div>
      <div style={{ marginBottom: 3 }}>
        <div style={{ height: 2, background: 'var(--bg5)', borderRadius: 1, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${fvg.filled * 100}%`, background: c, transition: 'width .3s' }} />
        </div>
        <span style={{ fontSize: 10, color: 'var(--t3)' }}>{(fvg.filled * 100).toFixed(0)}% filled</span>
      </div>
      <div className="inferred">Gap: {fvg.size.toFixed(2)} pts. Midpoint: {fvg.midpoint.toFixed(2)}</div>
    </div>
  );
}

// ─── OB row ───────────────────────────────────────────────────────────────────

function OBRow({ ob, curPrice }: { ob: OrderBlock; curPrice: number }) {
  const isBull = ob.type === 'bullish';
  const c = isBull ? 'var(--green)' : 'var(--red)';
  const distPct = (Math.abs(curPrice - ob.midpoint) / curPrice * 100).toFixed(2);
  return (
    <div className="card-inner" style={{ borderLeft: `2px solid ${c}`, marginBottom: 4 }}>
      <div className="fsb" style={{ marginBottom: 3 }}>
        <div className="flex gap6">
          <span className={`badge ${isBull ? 'b-green' : 'b-red'}`} style={{ fontSize: 10 }}>{isBull ? 'Bull' : 'Bear'} OB</span>
          <span className={`badge ${ob.strength === 'major' ? 'b-amber' : 'b-dim'}`} style={{ fontSize: 10 }}>{ob.strength}</span>
          <span className="mono" style={{ fontSize: 12, color: c }}>{ob.bottom.toFixed(2)}–{ob.top.toFixed(2)}</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--t3)' }}>{distPct}% away</span>
      </div>
      <div className="inferred">{ob.description}</div>
    </div>
  );
}

// ─── Premium/Discount gauge ───────────────────────────────────────────────────

function PDGauge({ pd }: { pd: NonNullable<SMCResult['premiumDiscount']> }) {
  const zoneColor = pd.zone === 'premium' ? 'var(--red)' : pd.zone === 'discount' ? 'var(--green)' : 'var(--amber)';
  const pct = Math.max(0, Math.min(100, pd.positionPct));

  return (
    <div className="card-inner" style={{ marginBottom: 8 }}>
      <div className="fsb mb6">
        <div>
          <span style={{ fontSize: 13, fontWeight: 600, color: zoneColor, textTransform: 'capitalize' }}>
            {pd.zone === 'equilibrium' ? 'Equilibrium' : pd.zone === 'premium' ? 'Premium Zone' : 'Discount Zone'}
          </span>
          <span style={{ fontSize: 11, color: 'var(--t3)', marginLeft: 8 }}>{pct.toFixed(0)}% of range</span>
        </div>
        <span className="mono" style={{ fontSize: 12, color: 'var(--amber)' }}>EQ: {pd.equilibrium.toFixed(2)}</span>
      </div>

      {/* Range gauge */}
      <div style={{ position: 'relative', height: 20, background: 'var(--bg4)', borderRadius: 4, overflow: 'visible', marginBottom: 6 }}>
        {/* Premium zone (top 37.5%) */}
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '37.5%', background: 'rgba(255,62,94,.18)', borderRadius: '0 4px 4px 0' }} />
        {/* Discount zone (bottom 37.5%) */}
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '37.5%', background: 'rgba(15,232,137,.18)', borderRadius: '4px 0 0 4px' }} />
        {/* Equilibrium line */}
        <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: 'rgba(255,176,32,.5)' }} />
        {/* Price marker */}
        <div style={{
          position: 'absolute', left: `${pct}%`, top: -2, bottom: -2,
          width: 3, background: 'var(--blue)', borderRadius: 2,
          transform: 'translateX(-50%)',
          boxShadow: '0 0 6px rgba(45,156,219,.6)',
        }} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--t3)' }}>
        <span style={{ color: 'rgba(15,232,137,.7)' }}>Discount {pd.rangeLow.toFixed(2)}</span>
        <span style={{ color: 'rgba(255,176,32,.7)' }}>EQ</span>
        <span style={{ color: 'rgba(255,62,94,.7)' }}>Premium {pd.rangeHigh.toFixed(2)}</span>
      </div>
      <div className="inferred mt4">{pd.description}</div>
    </div>
  );
}

// ─── Main SMC Panel ───────────────────────────────────────────────────────────

interface SMCPanelProps {
  symbol: string;
  overlays: SMCOverlaySettings;
  onOverlaysChange: (o: SMCOverlaySettings) => void;
}

export function SMCPanel({ symbol, overlays, onOverlaysChange }: SMCPanelProps) {
  const { symbolData, settings } = useStore();
  const data = symbolData[symbol];
  const [section, setSection] = useState<'overview' | 'structure' | 'zones' | 'sweeps'>('overview');

  if (!data?.candles?.length) return (
    <div className="card">
      <div className="sl">Smart Money Concepts</div>
      <div className="empty">
        <div className="icon">🧠</div>
        <h3>No Data</h3>
        <p>Import OHLCV data to enable SMC analysis.</p>
      </div>
    </div>
  );

  const smc = analyzeSMC(data.candles, settings);
  if (!smc) return (
    <div className="card">
      <div className="sl">Smart Money Concepts</div>
      <div className="empty"><div className="icon">⏳</div><h3>Insufficient Data</h3><p>Need at least 15 candles for SMC analysis.</p></div>
    </div>
  );

  const curPrice = data.candles[data.candles.length - 1].close;
  const biasColor = smc.bias === 'bullish' ? 'var(--green)' : smc.bias === 'bearish' ? 'var(--red)' : 'var(--amber)';

  return (
    <div className="card">
      {/* Header */}
      <div className="fsb mb10">
        <div>
          <div className="sl" style={{ marginBottom: 4 }}>Smart Money Concepts — {symbol}</div>
          <BiasIndicator bias={smc.bias} strength={smc.biasStrength} />
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 2 }}>SMC Confidence</div>
          <div className="mono" style={{ fontSize: 22, fontWeight: 700, color: biasColor }}>{smc.confidenceScore}%</div>
        </div>
      </div>

      {/* Key signal callout */}
      <div style={{ padding: '8px 12px', background: 'var(--bg3)', borderRadius: 6, borderLeft: `3px solid ${biasColor}`, marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 3 }}>Key Signal</div>
        <div style={{ fontSize: 13, color: 'var(--t0)', lineHeight: 1.5 }}>{smc.keySignal}</div>
      </div>

      {/* Chart overlay toggles */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 5 }}>Chart Overlays</div>
        <OverlayToggles overlays={overlays} onChange={onOverlaysChange} />
      </div>

      <div className="div" />

      {/* Sub-tab navigation */}
      <div className="tab-row" style={{ marginBottom: 10 }}>
        {[
          ['overview', 'Overview'],
          ['structure', `Structure (${smc.structureEvents.length})`],
          ['zones', `FVG/OB (${smc.allFVGs.length + smc.allOBs.length})`],
          ['sweeps', 'Sweeps'],
        ].map(([id, label]) => (
          <button key={id} className={`tab ${section === id ? 'on' : ''}`} onClick={() => setSection(id as any)}>
            {label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {section === 'overview' && (
        <div>
          {/* Metrics grid */}
          <div className="g3" style={{ gap: 6, marginBottom: 10 }}>
            {[
              { l: 'Structure', v: smc.lastBOS ? (smc.lastBOS.dir === 'bullish' ? '▲ BOS Bull' : '▼ BOS Bear') : 'No BOS', c: smc.lastBOS?.dir === 'bullish' ? 'var(--green)' : smc.lastBOS ? 'var(--red)' : 'var(--t2)' },
              { l: 'CHOCH', v: smc.lastCHOCH ? (smc.lastCHOCH.dir === 'bullish' ? '▲ Bull' : '▼ Bear') : 'None', c: smc.lastCHOCH?.dir === 'bullish' ? 'var(--green)' : smc.lastCHOCH ? 'var(--red)' : 'var(--t3)' },
              { l: 'Sweep', v: smc.recentSweep ? (smc.recentSweep.reclaimed ? '✓ Reclaimed' : 'Active') : 'None', c: smc.sweepAndReverse ? 'var(--cyan)' : smc.recentSweep ? 'var(--amber)' : 'var(--t3)' },
              { l: 'Displacement', v: smc.recentDisplacement ? (smc.recentDisplacement.dir === 'bullish' ? '▲ Bull' : '▼ Bear') : 'None', c: smc.displacementAligned ? 'var(--purple)' : smc.recentDisplacement ? 'var(--t1)' : 'var(--t3)' },
              { l: 'FVG Above', v: smc.fvgAbove ? smc.fvgAbove.bottom.toFixed(2) : '—', c: smc.fvgAbove ? 'var(--red)' : 'var(--t3)' },
              { l: 'FVG Below', v: smc.fvgBelow ? smc.fvgBelow.top.toFixed(2) : '—', c: smc.fvgBelow ? 'var(--green)' : 'var(--t3)' },
              { l: 'OB Above', v: smc.obAbove ? smc.obAbove.bottom.toFixed(2) : '—', c: smc.obAbove ? 'var(--red)' : 'var(--t3)' },
              { l: 'OB Below', v: smc.obBelow ? smc.obBelow.top.toFixed(2) : '—', c: smc.obBelow ? 'var(--green)' : 'var(--t3)' },
              { l: 'Zone', v: smc.premiumDiscount ? smc.premiumDiscount.zone : 'N/A', c: smc.premiumDiscount?.zone === 'premium' ? 'var(--red)' : smc.premiumDiscount?.zone === 'discount' ? 'var(--green)' : 'var(--amber)' },
            ].map(({ l, v, c }) => (
              <div key={l} className="card-inner">
                <div style={{ fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', marginBottom: 2 }}>{l}</div>
                <div className="mono" style={{ fontSize: 12, fontWeight: 600, color: c }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Premium/Discount gauge (always in overview) */}
          {smc.premiumDiscount && <PDGauge pd={smc.premiumDiscount} />}

          {/* Sweep + Reclaim alert */}
          {smc.sweepAndReverse && (
            <div style={{ padding: '8px 12px', background: 'rgba(34,211,238,.08)', border: '1px solid rgba(34,211,238,.25)', borderRadius: 6, marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: 'var(--cyan)', fontWeight: 600, marginBottom: 3 }}>⚡ Sweep + Reclaim Active</div>
              <div style={{ fontSize: 12, color: 'var(--t1)', lineHeight: 1.5 }}>{smc.recentSweep!.description}</div>
            </div>
          )}

          {/* CHOCH alert */}
          {smc.lastCHOCH && smc.lastCHOCH.candleIdx >= data.candles.length - 15 && (
            <div style={{ padding: '8px 12px', background: smc.lastCHOCH.dir === 'bullish' ? 'var(--green-bg)' : 'var(--red-bg)', border: `1px solid ${smc.lastCHOCH.dir === 'bullish' ? 'var(--green-bd)' : 'var(--red-bd)'}`, borderRadius: 6, marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: smc.lastCHOCH.dir === 'bullish' ? 'var(--green)' : 'var(--red)', fontWeight: 600, marginBottom: 3 }}>
                {smc.lastCHOCH.dir === 'bullish' ? '▲' : '▼'} CHOCH — Potential Reversal
              </div>
              <div style={{ fontSize: 12, color: 'var(--t1)', lineHeight: 1.5 }}>{smc.lastCHOCH.description}</div>
            </div>
          )}
        </div>
      )}

      {/* Structure events */}
      {section === 'structure' && (
        <div>
          {smc.structureEvents.length === 0 ? (
            <div className="empty" style={{ padding: '16px' }}><div style={{ fontSize: 22, opacity: .3 }}>📐</div><h3 style={{ fontSize: 14 }}>No Structure Events</h3><p>No BOS or CHOCH detected in recent price action.</p></div>
          ) : (
            smc.structureEvents.map((e, i) => <StructureRow key={i} event={e} />)
          )}
          <div className="disclaimer mt8">
            BOS = continuation in trend direction. CHOCH = first sign of reversal. Major events involve significant swing points; minor events are smaller pivots.
          </div>
        </div>
      )}

      {/* FVGs + Order Blocks */}
      {section === 'zones' && (
        <div>
          {smc.allFVGs.length > 0 && (
            <>
              <div className="sl" style={{ marginBottom: 6 }}>Fair Value Gaps ({smc.allFVGs.length})</div>
              {smc.allFVGs.map(f => <FVGRow key={f.id} fvg={f} curPrice={curPrice} />)}
            </>
          )}
          {smc.allOBs.length > 0 && (
            <>
              <div className="sl" style={{ margin: '10px 0 6px' }}>Order Blocks ({smc.allOBs.length})</div>
              {smc.allOBs.map(ob => <OBRow key={ob.id} ob={ob} curPrice={curPrice} />)}
            </>
          )}
          {!smc.allFVGs.length && !smc.allOBs.length && (
            <div className="empty" style={{ padding: '16px' }}><div style={{ fontSize: 22, opacity: .3 }}>◻</div><h3 style={{ fontSize: 14 }}>No Open Zones</h3><p>No unmitigated FVGs or order blocks detected with current sensitivity settings.</p></div>
          )}
          <div className="disclaimer mt8">
            FVGs = 3-candle imbalances where market moved too fast to fill all orders. OBs = last opposing candle before institutional displacement. Both act as high-probability reaction zones.
          </div>
        </div>
      )}

      {/* Sweeps + Displacement */}
      {section === 'sweeps' && (
        <div>
          <div className="sl mb6">Recent Liquidity Sweeps</div>
          {smc.recentSweep ? (
            <div className="card-inner" style={{ borderLeft: `2px solid ${smc.sweepAndReverse ? 'var(--cyan)' : 'var(--amber)'}`, marginBottom: 8 }}>
              <div className="fsb mb4">
                <div className="flex gap6">
                  <span className={`badge ${smc.sweepAndReverse ? 'b-cyan' : 'b-amber'}`} style={{ fontSize: 10 }}>
                    {smc.recentSweep.type === 'sweep_high' ? '▲ HIGH' : '▼ LOW'}
                  </span>
                  {smc.recentSweep.reclaimed && <span className="badge b-green" style={{ fontSize: 10 }}>RECLAIMED</span>}
                </div>
                <span className="mono" style={{ fontSize: 12, color: 'var(--amber)' }}>{smc.recentSweep.level.toFixed(2)}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--t1)', lineHeight: 1.5 }}>{smc.recentSweep.description}</div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--t3)', marginBottom: 10 }}>No recent sweep detected.</div>
          )}

          <div className="sl mb6">Displacement</div>
          {smc.recentDisplacement ? (
            <div className="card-inner" style={{ borderLeft: `2px solid ${smc.recentDisplacement.dir === 'bullish' ? 'var(--green)' : 'var(--red)'}` }}>
              <div className="fsb mb4">
                <span className={`badge ${smc.recentDisplacement.dir === 'bullish' ? 'b-green' : 'b-red'}`} style={{ fontSize: 10 }}>
                  {smc.recentDisplacement.dir === 'bullish' ? '▲ BULL' : '▼ BEAR'} DISP
                </span>
                <div className="flex gap6">
                  <span style={{ fontSize: 11, color: 'var(--t3)' }}>{smc.recentDisplacement.size.toFixed(2)}% body</span>
                  <span style={{ fontSize: 11, color: 'var(--t3)' }}>{smc.recentDisplacement.volumeMultiple.toFixed(1)}x vol</span>
                  {smc.displacementAligned && <span className="badge b-purple" style={{ fontSize: 10 }}>ALIGNED</span>}
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--t1)', lineHeight: 1.5 }}>{smc.recentDisplacement.description}</div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--t3)' }}>No recent displacement detected.</div>
          )}

          <div className="disclaimer mt8">
            Sweep = stop run that creates liquidity for institutions. Displacement = impulsive move driven by large order flow, often creates FVG. Together = highest-conviction SMC entry signal.
          </div>
        </div>
      )}

      {/* Sensitivity setting */}
      <div className="div" />
      <div className="flex gap8" style={{ alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--t3)' }}>Sensitivity:</span>
        {(['low', 'medium', 'high'] as const).map(s => (
          <button
            key={s}
            className={`btn btn-sm ${settings.smcSensitivity === s ? 'btn-primary' : ''}`}
            style={{ fontSize: 11, textTransform: 'capitalize' }}
            onClick={() => useStore.getState().setSettings({ smcSensitivity: s })}
          >
            {s}
          </button>
        ))}
        <span style={{ fontSize: 11, color: 'var(--t3)', marginLeft: 4 }}>
          {settings.smcSensitivity === 'high' ? 'More signals, more noise' : settings.smcSensitivity === 'low' ? 'Fewer, higher-quality signals' : 'Balanced'}
        </span>
      </div>
    </div>
  );
}
